import type { ApiErrorPayload, RefreshRequest } from "@/api/types";

const DEFAULT_API_BASE =
  typeof window !== "undefined" ? window.location.origin : "http://localhost:8080";

export const API_BASE = import.meta.env.VITE_API_URL || DEFAULT_API_BASE;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  raw?: boolean;
  signal?: AbortSignal;
  redirect?: RequestRedirect;
}

interface ClientBindings {
  getAccessToken: () => string | null;
  getRefreshContext: () => RefreshRequest | null;
  setAccessToken: (token: string | null) => void;
  clearSession: () => void;
}

class ApiClient {
  private bindings: ClientBindings = {
    getAccessToken: () => null,
    getRefreshContext: () => null,
    setAccessToken: () => undefined,
    clearSession: () => undefined,
  };

  bind(bindings: Partial<ClientBindings>) {
    this.bindings = { ...this.bindings, ...bindings };
  }

  private getUnauthorizedRedirectPath() {
    if (typeof window === "undefined") {
      return "/login";
    }

    return window.location.pathname.startsWith("/admin") ? "/admin/login" : "/login";
  }

  async request<T>(method: string, path: string, options: RequestOptions = {}, retryOnUnauthorized = true): Promise<T> {
    const url = new URL(path, API_BASE);

    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (typeof value === "undefined") {
          return;
        }
        url.searchParams.set(key, String(value));
      });
    }

    const headers: Record<string, string> = {
      Accept: "application/json",
      ...options.headers,
    };

    const accessToken = this.bindings.getAccessToken();
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    if (typeof options.body !== "undefined") {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(url.toString(), {
      method,
      headers,
      body: typeof options.body === "undefined" ? undefined : JSON.stringify(options.body),
      credentials: "include",
      signal: options.signal,
      redirect: options.redirect,
    });

    if (response.status === 401 && retryOnUnauthorized && path !== "/v1/auth/refresh") {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        return this.request<T>(method, path, options, false);
      }
      this.bindings.clearSession();
      window.location.href = this.getUnauthorizedRedirectPath();
      throw new ApiError(401, "UNAUTHORIZED", "Session expired");
    }

    if (!response.ok) {
      const fallback = { error: { code: "UNKNOWN", message: response.statusText } } satisfies ApiErrorPayload;
      const payload = (await response.json().catch(() => fallback)) as ApiErrorPayload;
      throw new ApiError(response.status, payload.error.code, payload.error.message);
    }

    if (options.raw) {
      return response as unknown as T;
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  get<T>(path: string, params?: RequestOptions["params"], signal?: AbortSignal) {
    return this.request<T>("GET", path, { params, signal });
  }

  post<T>(path: string, body?: unknown, signal?: AbortSignal) {
    return this.request<T>("POST", path, { body, signal });
  }

  patch<T>(path: string, body?: unknown, signal?: AbortSignal) {
    return this.request<T>("PATCH", path, { body, signal });
  }

  delete<T>(path: string, signal?: AbortSignal) {
    return this.request<T>("DELETE", path, { signal });
  }

  async resolveRedirectUrl(
    path: string,
    params?: RequestOptions["params"],
    signal?: AbortSignal,
    retryOnUnauthorized = true,
  ): Promise<string> {
    const url = new URL(path, API_BASE);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (typeof value === "undefined") {
          return;
        }
        url.searchParams.set(key, String(value));
      });
    }

    const headers: Record<string, string> = {
      Accept: "application/json",
    };

    const accessToken = this.bindings.getAccessToken();
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers,
      credentials: "include",
      signal,
      redirect: "manual",
    });

    if (response.status === 401 && retryOnUnauthorized && path !== "/v1/auth/refresh") {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        return this.resolveRedirectUrl(path, params, signal, false);
      }
      this.bindings.clearSession();
      window.location.href = this.getUnauthorizedRedirectPath();
      throw new ApiError(401, "UNAUTHORIZED", "Session expired");
    }

    const location = response.headers.get("location");
    if (location) {
      return new URL(location, API_BASE).toString();
    }

    if (response.ok) {
      return response.url;
    }

    const fallback = { error: { code: "UNKNOWN", message: response.statusText } } satisfies ApiErrorPayload;
    const payload = (await response.json().catch(() => fallback)) as ApiErrorPayload;
    throw new ApiError(response.status, payload.error.code, payload.error.message);
  }

  async refreshToken(): Promise<boolean> {
    const refreshContext = this.bindings.getRefreshContext();
    if (!refreshContext) {
      return false;
    }

    try {
      const response = await this.request<{ access_token: string } & Record<string, unknown>>(
        "POST",
        "/v1/auth/refresh",
        { body: refreshContext },
        false,
      );
      this.bindings.setAccessToken(response.access_token);
      return true;
    } catch {
      return false;
    }
  }
}

export const api = new ApiClient();
